import { Auth } from 'aws-amplify';
import config from '../config';

/**
 * Enum of user states.
 * See also:
 * https://docs.aws.amazon.com/cognito/latest/developerguide/signing-up-users-in-your-app.html
 */
export const userStates = {
  INITIALISING:          'INITIALISING',    // Initial state.
  UNAUTHENTICATED:       'UNAUTHENTICATED', // Not signed in.
  // these are from AWS:
  UNCONFIRMED:           'UNCONFIRMED',     // Account exists, email not verified.
  CONFIRMED:             'CONFIRMED',       // The only valid state for use.
  ARCHIVED:              'ARCHIVED',
  COMPROMISED:           'COMPROMISED',
  UNKNOWN:               'UNKNOWN',
  RESET_REQUIRED:        'RESET_REQUIRED',
  FORCE_CHANGE_PASSWORD: 'FORCE_CHANGE_PASSWORD'
}

/**
 * User types as bitfield.
 * 
 * 1 0 0 0 0 0 0 0
 * │ │ │ │ │ │ │ └ guest
 * │ │ │ │ │ │ └── not used
 * │ │ │ │ │ └──── not used
 * │ │ │ │ └────── administrator
 * │ │ │ └──────── paraplanner
 * │ │ └────────── ifa
 * │ └──────────── staff
 * └────────────── member
 */
export const userTypes = {
  none          : 0b00000000,  
  member        : 0b10000000,
  staff         : 0b01000000,
  ifa           : 0b00100000,
  paraplanner   : 0b00010000,
  administrator : 0b00001000,
  undiff        : 0b00000010,
  guest         : 0b00000001,
  all           : 0b11111111,
}

/**
 * Tests whether a user has access by type.
 *
 * If authorisedTypes is empty no usertypes will have access.
 * @param {int} userType
 *    Type of logged in user (in context).
 * @param {int[]} authorisedTypes 
 *    List of all types that should have access.
 */
export function isAccessPermitted(userType = 0, authorisedTypes = [userTypes.all]) {
  // This is dodgy... Not a valid type for a user.
  if (userType === userTypes.all) {
    return false;
  }
  // Otherwise check if we have a permissions match.
  for (let i = 0; i < authorisedTypes.length; i++) {
    // Bitwise and the types. If one of the bitflags matches we'll evaluate
    // to true.
    if (authorisedTypes[i] & userType) {
      return true;
    }
  }
  // If we haven't matched anything deny permissions.
  return false;
}

/**
 * Tests whether a user has access by department.
 *
 * If authorisedDepartments is empty no usertypes will have access.
 * If the user is NOT CFS staff false will always be returned.
 *
 * @param {string} providerName
 *    Name of provider used for federation.
 * @param {string} userDepartment
 *    Department user is in.
 * @param {string[]} authorisedDepartments 
 *    List of all departments that should have access. Add 'all' to give all
 *    departments access.
 */
 export function isAccessPermittedByDepartment(
   providerName = '',
   userDepartment = '',
   authorisedDepartments = ['all']
  ) {
  // Only CFS users have a department (we can trust)
  if (providerName !== 'Cirencester-Friendly') {
    return false;
  }
  // Otherwise check if we have a permissions match.
  for (let i = 0; i < authorisedDepartments.length; i++) {
    // Compare the departments, if we have a match return true.
    // If 'all' departments are in the permitted list return true.
    if (
      authorisedDepartments[i] === 'all' ||
      authorisedDepartments[i] === userDepartment
    ) {
      return true;
    }
  }
  // If we haven't matched anything deny permissions.
  return false;
}

/**
 * Determines whether a user is federated by looking at groups in the session
 * id token. Matches against list of groups from settings.
 *
 * @param {*} session
 *    User session to inspect.
 */
export function getUserIsFederatedFromSession(session) {
  const idToken = session.getIdToken();
  if(
    idToken.payload['cognito:groups']
  ) {
    const groups = idToken.payload['cognito:groups'];
    for(let i = 0; i < groups.length; i++) {
      if(config.oauth.federatedGroups.includes(groups[i])) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Calls a callback with the user's email address.
 * @param {setUserEmailCallback} callback 
 *     Callback to call with the email addres as parameter.
 */
/**
 * @callback  setUserEmailCallback
 * @param {string} email.
 *     Email address for session.
 */
export function setUserEmail(callback) {
  Auth.currentSession()
    .then(session => {
      const userIsFederated = getUserIsFederatedFromSession(session);
      const idToken = session.getIdToken();
      let email = '';
      if(userIsFederated === null) {
        console.error('Received user federated null in getEmail');
        callback('');
      }
      else if(
        userIsFederated
        // && idToken.payload.identities[0]
      ) {
        email = idToken.payload.identities[0].userId;
      }
      else {
        email = idToken.payload.email;
      }
      callback(email);
    }
  )
  .catch(error => {
    callback('');
  });
}

/**
 * Calls a callback with the user's email address.
 * @param {setUserEmailCallback} callback 
 *     Callback to call with the email addres as parameter.
 */
/**
 * @callback  setUserEmailCallback
 * @param {string} email.
 *     Email address for session.
 * @param {string} department.
 *     Department for session (only set for federated).
 * @param {string} providerName.
 *     Provider name for session (only set for federated).
 */
 export function setUserEmailDepartmentAndProvider(callback) {
  Auth.currentSession()
    .then(session => {
      const userIsFederated = getUserIsFederatedFromSession(session);
      const idToken = session.getIdToken();
      let email = '';
      let department = '';
      let providerName = '';
      if(userIsFederated === null) {
        console.error('Received user federated null in setUserEmailDepartmentAndProvider');
        callback('', '', '');
      }
      else if(
        userIsFederated
        // && idToken.payload.identities[0]
      ) {
        email = idToken.payload.identities[0].userId;
        providerName = idToken.payload.identities[0].providerName;
        // Only set department for CFS.
        if (providerName === "Cirencester-Friendly") {
          department = idToken.payload['custom:department']
        }
      }
      else {
        email = idToken.payload.email;
      }
      callback(email, department, providerName);
    }
  )
  .catch(error => {
    callback('', '', '');
  });
}

/**
 * Calls a callback with the user's profiles in an array
 * @param {setProfilesCallback} callback 
 *     Callback to call with a user profiles array.
 */
/**
 * @callback setProfilesCallback
 *     Calls callback function with an array of user profiles.
 */
export function setUserProfiles(callback) {
  Auth.currentSession()
  .then(session => {
    callback(getUserProfilesFromSession(session));
  })
  .catch(error => {
    console.error(error);
    callback([]);
  });
}

/**
 * Parses profiles from a session into an array to be returned.
 *
 * @param {CognitoUserSession} session
 *    Cognito session to aprse profiles from.
 *
 * @returns {[Object]}
 *    List of user profiles [{uid: <uid>, name: <name>}]
 */
export function getUserProfilesFromSession(session) {
  const idToken = session.getIdToken();
    if (!idToken.payload.hasOwnProperty('custom:profiles')) {
      return([]);
    }
    const profilesObject = JSON.parse(idToken.payload['custom:profiles']);
    let profiles = [];
    for (const [key, value] of Object.entries(profilesObject)) {
      profiles.push(
        {
          uid: key,
          name: value,
        }
      );
    }
    return(profiles);
}

/**
 * Calls a callback with whether the user is federated or not.
 * @param {setUserFederatedInfoCallback} callback 
 *     Callback to call with the result. Will set null on error.
 */
/**
 * @callback setUserFederatedInfoCallback
 * @param {bool} isfederated.
 *     Whether the user is federated.
 * @param {string} department.
 *     Department for session (only set for federated).
 * @param {string} providerName.
 *     Provider name for session (only set for federated).
 */
export function setUserFederatedInfo(callback) {
  Auth.currentSession()
    .then(session => {
      const userIsFederated = getUserIsFederatedFromSession(session);
      if(userIsFederated === null) {
        callback(false, '', '');
      }
      const idToken = session.getIdToken();
      let providerName = idToken.payload.identities[0].providerName;
      let department = '';
      // Only set department for CFS.
      if (providerName === "Cirencester-Friendly") {
        department = idToken.payload['custom:department']
      }
      callback(true, department, providerName);
    }
  )
  .catch(
    error => {
      // Take a safe default if we error.
      callback(false, '', '');
    }
  );
}

/**
 * Calls a callback with whether the user is federated or not.
 * @param {setUserIsFederatedCallback} callback 
 *     Callback to call with the result. Will set null on error.
 */
/**
 * @callback setUserIsFederatedCallback
 * @param {bool} isfederated.
 *     Whether the user is federated.
 */
 export function setUserIsFederatedFromGroups(callback) {
  Auth.currentSession()
    .then(session => {
      callback(getUserIsFederatedFromSession(session));
    }
  )
  .catch(
    error => {
      // Take a safe default if we error.
      callback(false);
    }
  );
}

/**
 * Returns stirng representation of user type.
 *
 * @param {int} userType 
 * @returns {string}
 *    String representaiton of user type.
 */
export function userTypeAsString(userType) {
  switch(userType) {
    case userTypes.none:
      return('None');
    case userTypes.guest:
      return('Guest');
    case userTypes.undiff:
      return('Undifferentiated ifalike');
    case userTypes.member:
      return('Member');
    case userTypes.staff:
      return('Staff');
    case userTypes.ifa:
      return('Adviser');
    case userTypes.paraplanner:
      return('Paraplanner');
    case userTypes.administrator:
      return('Administrator');
    case userTypes.all:
      return('All');
    default:
      return('Adviser');
  }
}

/**
 * 
 * @param {string} userTypeString
 *    String to parse into a usertype.
 * @param {int} defaultReturn
 *    Default to return when parsing failed.
 */
export function userTypeFromString(userTypeString, defaultReturn = 0) {
  if(typeof(userTypeString) !== "string") {
    return(defaultReturn); 
  }
  switch(userTypeString.toLowerCase()) {
    case 'none':
      return(userTypes.none);
    case 'guest':
      return(userTypes.guest);
    case 'undifferentiated ifalike':
      return(userTypes.undiff);
    case 'member':
      return(userTypes.member);
    case 'staff':
      return(userTypes.staff);
    case 'adviser':
      return(userTypes.ifa);
    case 'paraplanner':
      return(userTypes.paraplanner);
    case 'administrator':
      return(userTypes.administrator);
    case 'all':
      return(userTypes.all);
    default:
      // Maybe we were passed an int string?
      const asInt = parseInt(userTypeString);
      if (asInt > 0 && asInt < 255) {
        return asInt;
      }
      return(defaultReturn);
  }
}

export function userBitmaskToStringArray(bitmask = '00000000') {
  // Type is stored as bitflags. Parse it here.
  // Convert type to an int.
  let result = [];
  const userTypeInt = parseInt(bitmask, 2);
  if (isNaN(userTypeInt)) {
    result.push('INVALID');
  }
  else {
    // Do some bitwise compares.
    if(userTypeInt & 0b10000000) {
      result.push('member');
    }
    if(userTypeInt & 0b01000000) {
      result.push('staff');
    }
    if(userTypeInt & 0b00100000) {
      result.push('ifa');
    }
    if(userTypeInt & 0b00010000) {
      result.push('paraplanner');
    }
    if(userTypeInt & 0b00001000) {
      result.push('administrator');
    }
    if(userTypeInt & 0b00000001) {
      result.push('guests');
    }
    if(userTypeInt === 0) {
      result.push('none');
    }
  }
  return result;
}
